import { Token } from '../models/token.model';
import { inject, Injectable } from '@angular/core';
import { ILoginRequest } from '../dtos/requests/login.request';
import { ErrorOrError } from '../../../../core/results/error-or/models/error-or-error.model';
import { IToken } from '../dtos/responses/token.response';
import { Router } from '@angular/router';
import { UserContextService } from './user-context.service';
import { CurrentUsersApiClient } from '../../../account-management/users/clients/current-users-api.client';
import { IUserContext } from '../models/user-context.model';
import { catchError, finalize, map, Observable, shareReplay, switchMap, tap } from 'rxjs';
import { IIdentityAbiClient } from '../clients/identity-api-client.interface';
import { IdentityApiClientProvider } from '../clients/identity-api-client-provider';
import { ToastService } from '../../../../../trash/shared/components/toasters/Toast.service';
import { UserUiServiceV2 } from '../../../../../shared';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  identityApiClient: IIdentityAbiClient = inject(IdentityApiClientProvider).getClient();
  currentUsersApiClient = inject(CurrentUsersApiClient);
  toastService = inject(ToastService);
  router = inject(Router);
  private refreshAccessTokenRequest?: Observable<Token>;

  authenticate(request: ILoginRequest, isPersist: boolean = false): Observable<IUserContext> {
    return this.identityApiClient.login(request).pipe(
      switchMap((token) => {
        this.handleLoginSuccess(token, isPersist);
        return this.currentUsersApiClient.get();
      }),
      tap((userContext) => {
        // Handle success case
        this.handleUserContextSuccess(userContext);
      }),
      catchError((errors: ErrorOrError[]) => {
        // Handle error case
        // handleApiErrors(errors, this.toastService, [invalidCredentialsError]);
        this.logout();
        throw errors; // Re-throw so component can also handle the error
      })
    );
  }

  authenticateRegistration(request: any, isPersist: boolean = false): Observable<IUserContext> {
    return this.identityApiClient.register(request).pipe(
      switchMap((token) => {
        this.handleLoginSuccess(token, isPersist);
        return this.currentUsersApiClient.get();
      }),
      tap((userContext) => {
        this.handleUserContextSuccess(userContext);
      }),
      catchError((errors: ErrorOrError[]) => {
        // handleApiErrors(errors, this.toastService, [invalidCredentialsError]);
        // this.logout();
        throw errors;
      })
    );
  }

  ensureAuthenticated(): Token | undefined {
    const token = UserContextService.getToken();

    if (!token || token.isExpired()) {
      UserContextService.remove();
      return undefined;
    }

    return token;
  }

  // Shares one refresh call between concurrent requests while the access token is expired.
  refreshAccessToken(token: Token): Observable<Token> {
    if (this.refreshAccessTokenRequest) {
      return this.refreshAccessTokenRequest;
    }

    const wasPersisted = token.isPersisted;

    this.refreshAccessTokenRequest = this.identityApiClient
      .refresh(token.toRefreshTokenRequest())
      .pipe(
        switchMap((newToken) => {
          const refreshedToken = Token.create({ ...newToken, isPersisted: wasPersisted });
          UserContextService.setToken(refreshedToken, wasPersisted);
          return this.currentUsersApiClient.get();
        }),
        tap((userContext) => UserContextService.setUserStorage(userContext)),
        map(() => UserContextService.getToken() as Token),
        finalize(() => {
          this.refreshAccessTokenRequest = undefined;
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.refreshAccessTokenRequest;
  }

  logout(): void {
    UserContextService.remove();
    this.router.navigate(['auth/login']);
  }

  handleLoginSuccess(token: IToken, isPersist: boolean): void {
    UserContextService.setToken(token, isPersist);
  }

  handleUserContextSuccess(userContext: IUserContext): void {
    UserContextService.setUserStorage(userContext);
    this.toastService.showSuccess('Login Successful', 'You have been logged in successfully. Welcome back!');
    this.router.navigate([UserUiServiceV2.getInitialPage()]);
  }
}
